import { useEffect, useState } from 'react'
import { Message } from '../../components/TextChatWindow/TextChatWindow'
import { GPT_LOGO } from '../../Subnets/Cortext/types'
import { useParams } from 'react-router-dom'
import { useAppModeContext } from 'context/AppModeContext'

export type Chat = { id: string; messages: Message[] }
export type ChatHistory = Chat[]

const generateRandomId = () => {
    return Math.random().toString(36).substring(2, 15)
}

const useChatHistory = () => {
    const { account_id } = useAppModeContext()

    const { id } = useParams()
    const chatHistoryStorage = `chatHistory-${id}-${account_id}`

    const [chat, setChat] = useState<Chat>({
        id: generateRandomId(),
        messages: [],
    })

    const [chatHistory, setChatHistory] = useState<ChatHistory>(() =>
        JSON.parse(localStorage.getItem(chatHistoryStorage) || '[]'),
    )

    const currentDate = new Date().toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
    })

    const updateMessagesAndHistory = (
        text: string,
        model: string,
        streamed?: boolean,
        curlString?: string,
        prompt?: string,
    ) => {
        const newMessage = {
            icon: GPT_LOGO,
            message: text,
            date: currentDate,
            name: model,
            curlString: curlString || '',
            prompt: prompt || '',
        }

        setChat((prevState: Chat) => {
            let newMessageHistory
            if (
                streamed &&
                prevState.messages.length > 0 &&
                prevState.messages[prevState.messages.length - 1].name === model
            ) {
                // Replace the text of the last message if the model name is the same
                newMessageHistory = {
                    id: prevState.id,
                    messages: prevState.messages.map((msg, index) =>
                        index === prevState.messages.length - 1 ? { ...msg, message: text } : msg,
                    ),
                }
            } else {
                // Add a new message
                newMessageHistory = {
                    id: prevState?.id || generateRandomId(),
                    messages: [...(prevState?.messages || []), newMessage],
                }
            }

            setChatHistory((prevState: ChatHistory) => {
                const existingIndex = prevState.findIndex(item => item.id === newMessageHistory.id)
                let newState
                if (existingIndex !== -1) {
                    // Replace the existing item with the new messageHistory
                    newState = [
                        ...prevState.slice(0, existingIndex),
                        newMessageHistory,
                        ...prevState.slice(existingIndex + 1),
                    ]
                } else {
                    // If the item doesn't exist, add it to the array
                    newState = [newMessageHistory, ...prevState]
                }
                // Save to local storage
                localStorage.setItem(chatHistoryStorage, JSON.stringify(newState))

                return newState
            })

            return newMessageHistory
        })
    }

    const handleStartNewChat = () => {
        setChat({
            id: generateRandomId(),
            messages: [],
        })
    }

    const handleDeleteHistory = (id: string) => {
        const filteredChat = chatHistory.filter((item: Chat) => item.id !== id)

        const currentHistoryId = chat.id

        localStorage.setItem(chatHistoryStorage, JSON.stringify(filteredChat))
        setChatHistory(filteredChat)

        if (currentHistoryId === id) {
            handleStartNewChat()
        }
    }

    useEffect(() => {
        if (chatHistory?.length > 0) {
            setChat(chatHistory[0])
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        chat,
        chatHistory,
        updateMessagesAndHistory,
        handleStartNewChat,
        setChat,
        currentDate,
        handleDeleteHistory,
    }
}

export default useChatHistory
